import React, { useState, useEffect, useCallback } from 'react';
import { FilterBar, DataTable, useNamespaces, NamespaceSelect } from './LivePage';
import { useCluster } from '../context/ClusterContext';
import { useAuth } from '../context/AuthContext';
import { clustersAPI } from '../services/api';
import Pager from '../components/Pager';
import WorkloadDetailDrawer from '../components/WorkloadDetailDrawer';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';

/**
 * Shared shell for every Steve-paged resource page: the Workloads group (StatefulSets,
 * DaemonSets, Jobs, CronJobs), Configuration (ConfigMaps, Secrets, HPA, Resource Quotas,
 * Limit Ranges) and Access Control (Service Accounts, Roles, Bindings).
 *
 * Two problems it replaced. The Workloads four shared /workloads, which fetched ALL FOUR
 * kinds through the Python k8s SDK on every load — ~2.9s cluster-wide, so opening Jobs also
 * pulled 550 StatefulSets. ConfigMaps and Secrets shared /config-resources, which was worse:
 * 21.9s for all namespaces, dragging 1089 secrets into a page that shows configmaps.
 *
 * Four Configuration pages had no API call at all and rendered hardcoded placeholder rows as
 * though they were live cluster state.
 *
 * Shared rather than a dozen near-identical files: they differ only in columns and endpoint,
 * and keeping paging/filter/drawer logic in one place is what stops them drifting apart the
 * way the Workloads four already had.
 */
export default function PagedWorkloadPage({
  title,        // page heading, e.g. "StatefulSets"
  kind,         // Kubernetes kind for the detail drawer, e.g. "StatefulSet"
  resource,     // API path segment, e.g. "statefulsets"
  cols,         // column headings, excluding the trailing actions column
  renderRow,    // (item) => array of <td> for the columns above
  label,        // plural noun for the filter bar count
  // Whether rows get a Details button. False for kinds with no detail endpoint (quotas,
  // limit ranges, HPAs, RBAC objects) — a button that 400s is worse than no button.
  detail = true,
  // Cluster-scoped kinds (ClusterRoles, ClusterRoleBindings) have no namespace, so the
  // namespace selector is hidden rather than offering a filter that cannot apply.
  clusterScoped = false,
  // RBAC section gating the Delete button, and whether this kind has a delete endpoint at
  // all (statefulsets/daemonsets/jobs/cronjobs do; most Configuration/Access Control kinds
  // shown via this same shell do not, so the button is opt-in per page, not automatic).
  deletable = false,
  deleteSection = 'workloads',
  // Same opt-in shape as deletable, for kinds the generic restart endpoint actually
  // supports (StatefulSet/DaemonSet — Jobs/CronJobs/ConfigMaps/RBAC objects etc. have no
  // rollout concept, so this stays false for them rather than showing a button that 400s).
  restartable = false,
  // Opt-in create affordance — this shell has no built-in create modal (every kind has a
  // different body shape), so the page using it supplies its own:
  // renderCreate: ({ namespaces, ns, clusterId, refresh }) => JSX, rendered next to the
  // filter bar. Gated on createSection/"create" the same way delete/restart are gated.
  renderCreate,
  createSection = 'workloads',
}) {
  const { selectedCluster } = useCluster();
  const { hasSectionAction } = useAuth();
  const canDelete = deletable && hasSectionAction(deleteSection, 'delete');
  const canRestart = restartable && hasSectionAction(deleteSection, 'edit');
  const canCreate = !!renderCreate && hasSectionAction(createSection, 'create');
  const { namespaces, ns, setNs, resolved: nsResolved } = useNamespaces(selectedCluster?.id);
  const [nameFilter, setNameFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [restartingKey, setRestartingKey] = useState(null);

  const clusterId = selectedCluster?.id;

  const load = useCallback(async () => {
    if (!clusterId) return;
    // Wait for the namespace list to actually resolve before fetching a namespace-scoped
    // kind — `ns` starts '' while genuinely unresolved, which is indistinguishable from
    // "fetch with no filter" if read on its own. Firing early sent a real, slow
    // ALL-NAMESPACES query (same expensive shape fixed elsewhere this session) a beat
    // before the correct namespaced request, confirmed live via two /jobs/paged calls on
    // one page load — the first missing `namespace=` entirely. Cluster-scoped kinds
    // (ClusterRoles, PVs, StorageClasses) have no namespace to wait for, so they skip
    // this gate entirely.
    if (!clusterScoped && !nsResolved) return;
    setLoading(true); setError(null);
    try {
      const { data: d } = await clustersAPI.getWorkloadPaged(resource, clusterId, {
        page, pageSize, namespace: clusterScoped ? undefined : (ns || undefined),
      });
      setData({ items: d.items || [], total: d.total ?? 0 });
    } catch (e) {
      setError(e?.response?.status === 404
        ? 'Paged listing needs a managed cluster.'
        : (e?.response?.data?.detail || `Failed to load ${label}`));
      setData({ items: [], total: 0 });
    } finally { setLoading(false); }
  }, [clusterId, resource, page, pageSize, ns, clusterScoped, label, nsResolved]);

  useEffect(() => { load(); }, [load]);

  // Changing namespace or page size re-pages from the start; staying on page 9 of a filter
  // that now has 2 pages would show an empty table.
  useEffect(() => { setPage(1); }, [ns, pageSize, clusterId]);

  const handleDelete = useCallback(async (item) => {
    const key = `${item.namespace}/${item.name}`;
    if (!window.confirm(`Delete ${kind.toLowerCase()} "${item.name}" in namespace "${item.namespace}"? This cannot be undone.`)) return;
    setDeletingKey(key);
    try {
      await clustersAPI.deleteWorkload(resource, clusterId, item.namespace, item.name);
      setData(prev => ({
        items: prev.items.filter(i => `${i.namespace}/${i.name}` !== key),
        total: Math.max(0, prev.total - 1),
      }));
    } catch (e) {
      setError(e?.response?.data?.detail || `Failed to delete ${label}`);
    } finally {
      setDeletingKey(null);
    }
  }, [clusterId, resource, kind, label]);

  const handleRestart = useCallback(async (item) => {
    const key = `${item.namespace}/${item.name}`;
    if (!window.confirm(`Restart ${kind.toLowerCase()} "${item.name}" in namespace "${item.namespace}"? This rolls every pod.`)) return;
    setRestartingKey(key);
    try {
      await clustersAPI.restartWorkload(clusterId, resource, item.namespace, item.name);
    } catch (e) {
      setError(e?.response?.data?.detail || `Failed to restart ${label}`);
    } finally {
      setRestartingKey(null);
    }
  }, [clusterId, resource, kind, label]);

  if (!selectedCluster) {
    return (
      <div className="resource-page">
        <div className="page-header"><h1>{title}</h1></div>
        <div className="no-cluster-msg">No cluster selected.</div>
      </div>
    );
  }

  // Name filter is client-side on the current page only — Steve has no name-contains filter,
  // and the count says "of this page" so it cannot be mistaken for a cluster-wide search.
  const q = nameFilter.trim().toLowerCase();
  const filtered = q ? data.items.filter(i => (i.name || '').toLowerCase().includes(q))
                     : data.items;

  return (
    <div className="resource-page">
      <div className="page-header">
        <h1>{title} — {selectedCluster.name}</h1>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      <FilterBar count={filtered.length} total={data.total} label={label}>
        {!clusterScoped && (
          <NamespaceSelect namespaces={namespaces} ns={ns} onChange={setNs} />
        )}
        <input
          placeholder="Filter this page by name..."
          value={nameFilter}
          onChange={e => setNameFilter(e.target.value)}
        />
        {canCreate && renderCreate({ namespaces, ns, clusterId, refresh: load })}
      </FilterBar>

      <DataTable
        cols={(detail || canRestart || canDelete) ? [...cols, ''] : cols}
        emptyMsg={loading ? 'Loading…' : `No ${label} found`}
        rows={filtered.map(item => (
          <tr key={`${item.namespace}/${item.name}`}>
            {renderRow(item, { clusterId, refresh: load })}
            {(detail || canRestart || canDelete) && (
              <td>
                <ActionsMenu>
                  {detail && (
                    <button
                      style={MENU_ITEM}
                      onClick={() => setDetailFor({ namespace: item.namespace, name: item.name })}
                      title="Resources, ports, environment, volumes, events, YAML"
                    >
                      Details
                    </button>
                  )}
                  {canRestart && (
                    <button
                      style={MENU_ITEM}
                      disabled={restartingKey === `${item.namespace}/${item.name}`}
                      onClick={() => handleRestart(item)}
                      title="Rolling-restart every pod (kubectl rollout restart)"
                    >
                      {restartingKey === `${item.namespace}/${item.name}` ? 'Restarting…' : '↻ Restart'}
                    </button>
                  )}
                  {canDelete && (
                    <button
                      style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}
                      disabled={deletingKey === `${item.namespace}/${item.name}`}
                      onClick={() => handleDelete(item)}
                    >
                      {deletingKey === `${item.namespace}/${item.name}` ? 'Deleting…' : '🗑️ Delete'}
                    </button>
                  )}
                </ActionsMenu>
              </td>
            )}
          </tr>
        ))}
      />

      <Pager
        page={page}
        pageSize={pageSize}
        total={data.total}
        loading={loading}
        onPage={setPage}
        onPageSize={setPageSize}
      />

      {detailFor && (
        <WorkloadDetailDrawer
          clusterId={clusterId}
          kind={kind}
          resource={resource}
          namespace={detailFor.namespace}
          name={detailFor.name}
          onClose={() => setDetailFor(null)}
        />
      )}
    </div>
  );
}
